import React, { useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Compass, Check, ArrowLeft, ChevronLeft, ChevronRight, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AuthContext, API } from "@/App";
import { toast } from "sonner";

const PricingPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [billing, setBilling] = useState("monthly");

  const handleSubscribe = async (plan) => {
    if (!user) {
      toast.info("Please sign in first to subscribe");
      navigate("/register");
      return;
    }
    if (user.subscription_tier === plan) {
      toast.info("You're already on this plan");
      return;
    }
    setLoading(plan);
    try {
      const res = await fetch(`${API}/checkout/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ plan, billing, origin_url: window.location.origin })
      });
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.url;
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to create checkout");
      }
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  const plans = [
    {
      name: "Free", tier: "free",
      price: { monthly: "$0", yearly: "$0" }, period: { monthly: "forever", yearly: "forever" },
      description: "Get started with AI tool matching",
      features: ["10 searches/month", "Basic prompts only", "English only", "30-day search history"],
      included: [],
      cta: user ? (user.subscription_tier === "free" ? "Current Plan" : "Downgrade") : "Get Started Free",
      ctaAction: () => user ? null : navigate("/register"),
      disabled: user?.subscription_tier === "free", highlight: false
    },
    {
      name: "Pro", tier: "pro",
      price: { monthly: "$15", yearly: "$150" }, period: { monthly: "/month", yearly: "/year" },
      description: "For professionals who need more power",
      features: ["Unlimited searches", "Advanced prompts", "Chat with Promptviser", "Auto-save drafts", "Forever history", "Email support"],
      included: ["Everything in Free"],
      cta: user?.subscription_tier === "pro" ? "Current Plan" : "Upgrade to Pro",
      ctaAction: () => handleSubscribe("pro"),
      disabled: user?.subscription_tier === "pro", highlight: true, popular: true,
      savings: "Save 17%"
    },
    {
      name: "Premium", tier: "premium",
      price: { monthly: "$35", yearly: "$350" }, period: { monthly: "/month", yearly: "/year" },
      description: "Advanced AI with SEO & multi-language",
      features: ["Mega prompts (Chain-of-Thought)", "Agentic workflows", "Programmatic prompts (JSON)", "Agentic + Programmatic combined", "On-page SEO templates", "Off-page SEO guidance", "Translation + Auto-correct", "Multi-language UI (18 langs)", "News feed", "API access + Webhooks", "Priority support (4hr)"],
      included: ["Everything in Free + Pro"],
      cta: user?.subscription_tier === "premium" ? "Current Plan" : "Upgrade to Premium",
      ctaAction: () => handleSubscribe("premium"),
      disabled: user?.subscription_tier === "premium", highlight: false,
      savings: "Save 17%"
    },
    {
      name: "Team", tier: "team",
      price: { monthly: "$89", yearly: "$890" }, period: { monthly: "/mo for 5 seats", yearly: "/yr for 5 seats" },
      description: "Collaborate with your team",
      features: ["Everything in Premium", "5 seats included", "Shared prompt library", "Team analytics", "Approval workflows", "Seat management", "Export prompts as CSV", "API routing", "Dedicated support"],
      included: ["Everything in Free + Pro + Premium"],
      cta: user?.subscription_tier === "team" ? "Current Plan" : "Upgrade to Team",
      ctaAction: () => handleSubscribe("team"),
      disabled: user?.subscription_tier === "team", highlight: false,
      savings: "Save 17%"
    }
  ];

  return (
    <div className="min-h-screen bg-dark">
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white/60 hover:text-white"><ChevronLeft className="w-5 h-5" /></Button>
                <Button variant="ghost" size="icon" onClick={() => navigate(1)} className="text-white/60 hover:text-white"><ChevronRight className="w-5 h-5" /></Button>
              </div>
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-purple-700 flex items-center justify-center">
                  <Compass className="w-4 h-4 text-white" />
                </div>
                <span className="font-inter font-bold text-lg">promptviser</span>
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/"><Button variant="ghost" size="icon" className="text-white/60 hover:text-white"><Home className="w-5 h-5" /></Button></Link>
              {user ? (
                <Link to="/dashboard"><Button variant="outline" className="border-white/20 text-sm">Dashboard</Button></Link>
              ) : (
                <Link to="/login"><Button className="bg-purple-700 text-white hover:bg-purple-600 text-sm">Sign In</Button></Link>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <Link to="/" className="inline-flex items-center gap-2 text-white/40 hover:text-white mb-8 transition-colors text-sm">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        <div className="text-center mb-12">
          <h1 className="font-inter font-black text-4xl md:text-5xl mb-4">Choose Your Plan</h1>
          <p className="text-white/50 text-base max-w-2xl mx-auto">
            Higher tiers include ALL features from lower tiers. Upgrade or downgrade anytime.
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={() => setBilling("monthly")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${billing === "monthly" ? "bg-purple-700 text-white" : "text-white/50 hover:text-white"}`}
              data-testid="billing-monthly"
            >Monthly</button>
            <button
              onClick={() => setBilling("yearly")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${billing === "yearly" ? "bg-purple-700 text-white" : "text-white/50 hover:text-white"}`}
              data-testid="billing-yearly"
            >
              Yearly <span className="text-neon text-xs ml-1">Save 17%</span>
            </button>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid lg:grid-cols-4 md:grid-cols-2 sm:grid-cols-2 gap-4 max-w-6xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative p-5 rounded-2xl border ${
                plan.highlight ? "border-purple-500 pricing-highlight" : "border-white/10"
              } bg-dark-paper flex flex-col`}
              data-testid={`pricing-${plan.tier}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-purple-700 text-white text-xs font-bold rounded-full whitespace-nowrap">MOST POPULAR</div>
              )}
              <h3 className="font-inter font-semibold text-lg mb-1">{plan.name}</h3>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="font-inter font-black text-3xl">{plan.price[billing]}</span>
                <span className="text-white/30 text-xs">{plan.period[billing]}</span>
              </div>
              <p className="text-white/40 text-xs mb-4">{plan.description}</p>

              {plan.included.length > 0 && (
                <p className="text-purple-400 text-xs font-medium mb-2">{plan.included[0]}</p>
              )}

              <ul className="space-y-2 mb-5 flex-1">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="w-3.5 h-3.5 text-neon flex-shrink-0 mt-0.5" />
                    <span className="text-white/60 text-xs leading-relaxed">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                className={`w-full font-semibold text-sm ${
                  plan.highlight ? "bg-purple-700 text-white hover:bg-purple-600" : "bg-white/5 hover:bg-white/10 text-white/70"
                }`}
                onClick={plan.ctaAction}
                disabled={plan.disabled || loading === plan.tier}
                data-testid={`pricing-${plan.tier}-cta`}
              >
                {loading === plan.tier ? "Processing..." : plan.cta}
              </Button>
            </div>
          ))}
        </div>

        {/* FAQ */}
        <div className="max-w-3xl mx-auto mt-20">
          <h2 className="font-inter font-bold text-2xl text-center mb-8">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {[
              { q: "Can I cancel anytime?", a: "Yes! Cancel from your dashboard anytime. 7-day money-back guarantee for your first payment. See our Refund Policy." },
              { q: "What are the prompt tiers?", a: "Basic (Free), Advanced with role/context/tone (Pro), Chain-of-Thought mega prompts + Agentic workflows + JSON programmatic prompts (Premium and Team)." },
              { q: "Does every higher tier include lower tier features?", a: "Yes! Premium includes everything in Free + Pro. Team includes everything in Premium plus 5 seats and collaboration tools." },
              { q: "What is the yearly discount?", a: "17% off with yearly billing. Pro: $150/year, Premium: $350/year, Team: $890/year." },
              { q: "How does Team work?", a: "Team includes 5 seats with shared prompt library, team analytics, and approval workflows." }
            ].map((faq, i) => (
              <div key={i} className="bg-dark-paper border border-white/10 rounded-xl p-5">
                <h4 className="font-semibold text-sm mb-1.5">{faq.q}</h4>
                <p className="text-white/50 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center mt-12">
          <p className="text-white/40 text-sm">
            Questions? <a href="mailto:promptvisier.ai101@gmail.com" className="text-purple-400 hover:underline">promptvisier.ai101@gmail.com</a>
          </p>
        </div>
      </main>

      <footer className="border-t border-white/5 py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center text-white/30 text-xs">
          &copy; {new Date().getFullYear()} Promptviser. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
